import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { z } from "zod";
import type { InventoryRow, Section } from "../types";
import {
  readMonth,
  writeMonth,
  currentMonth,
  createRow,
  grandTotal,
} from "../services/inventory";
import UnitCombobox from "./UnitCombobox";

const rowSchema = z.object({
  item: z.string().min(1, "Item name is required"),
  quantity: z.number().positive("Quantity must be positive"),
  unit: z.string().min(1, "Unit is required"),
  unit_price: z.number().min(0, "Price cannot be negative"),
});

interface Props {
  section: Section;
  icon: LucideIcon;
  title: string;
}

export default function InventoryTable({ section, icon: Icon, title }: Props) {
  const month = currentMonth();
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [newItem, setNewItem] = useState("");
  const [newQty, setNewQty] = useState("1");
  const [newUnit, setNewUnit] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editField, setEditField] = useState<string>("");
  const [editVal, setEditVal] = useState("");
  const [confirmIdx, setConfirmIdx] = useState<number | null>(null);

  useEffect(() => {
    readMonth(month).then((data) => {
      setRows(data[section]);
    });
  }, [month, section]);

  const save = useCallback(
    async (updated: InventoryRow[]) => {
      setRows(updated);
      setSaveError(null);
      try {
        const data = await readMonth(month);
        data[section] = updated;
        await writeMonth(month, data);
      } catch (e: any) {
        setSaveError(e?.message || "Failed to save");
      }
    },
    [month, section]
  );

  const addRow = () => {
    setErrors([]);
    const parsed = rowSchema.safeParse({
      item: newItem.trim(),
      quantity: parseFloat(newQty) || 0,
      unit: newUnit.trim(),
      unit_price: parseFloat(newPrice) || 0,
    });
    if (!parsed.success) {
      setErrors(parsed.error.errors.map((e) => e.message));
      return;
    }
    const { item, quantity, unit, unit_price } = parsed.data;
    const row = createRow(item, quantity, unit, unit_price);
    save([...rows, row]);
    setNewItem("");
    setNewQty("1");
    setNewUnit("");
    setNewPrice("");
  };

  const deleteRow = (idx: number) => {
    save(rows.filter((_, i) => i !== idx));
    setConfirmIdx(null);
  };

  const startEdit = (idx: number, field: string, val: string | number) => {
    setEditingId(idx);
    setEditField(field);
    setEditVal(String(val));
  };

  const commitEdit = (idx: number) => {
    if (editingId !== idx) return;
    const updated = [...rows];
    const val = ["quantity", "unit_price", "total"].includes(editField)
      ? parseFloat(editVal.replace(/,/g, "")) || 0
      : editVal;
    (updated[idx] as any)[editField] = val;
    updated[idx].total = updated[idx].quantity * updated[idx].unit_price;
    save(updated);
    setEditingId(null);
  };

  const total = grandTotal(rows);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-citilux-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h1>
          <p className="text-sm text-gray-500 dark:text-metallic-silver/60">{month}</p>
        </div>
      </div>

      {/* Grand total (top right) */}

        {rows.length > 0 && (
          <div className="flex justify-end items-baseline gap-3 -mt-2">
            <span className="text-sm font-medium text-gray-500 dark:text-metallic-silver uppercase tracking-wider">
              Total {title}
            </span>
            <span className="text-2xl font-bold text-citilux-blue-600 dark:text-citilux-blue-400 tabular-nums">
              ₦{formatNum(total)}
            </span>
          </div>
        )}


      {/* Error banner */}
      {saveError && (
        <div className="flex items-center gap-2 bg-red-100 dark:bg-red-500/10 border border-red-300 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {saveError}
          <button onClick={() => setSaveError(null)} className="ml-auto text-red-500/60 hover:text-red-700 dark:hover:text-red-400">✕</button>
        </div>
      )}

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="flex flex-col gap-1 bg-amber-100 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 text-amber-700 dark:text-amber-400 text-sm rounded-xl px-4 py-3">
          {errors.map((e, i) => (
            <span key={i} className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {e}
            </span>
          ))}
        </div>
      )}

      {/* Add form */}
      <div className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-4 sm:p-5 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Item name"
            className="col-span-2 sm:col-span-1 bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500 placeholder:text-gray-400 dark:placeholder:text-white/30"
          />
          <input
            type="number"
            step="1"
            min="0"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value)}
            placeholder="Qty"
            className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500"
          />
          <UnitCombobox value={newUnit} onChange={setNewUnit} placeholder="Unit" />
          <input
            type="number"
            step="0.01"
            min="0"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            placeholder="Price"
            className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500"
          />
          <button
            onClick={addRow}
            className="flex items-center justify-center gap-2 bg-citilux-blue-600 hover:bg-citilux-blue-500 text-sm font-semibold text-white rounded-xl px-4 py-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-4 sm:-mx-0">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="border-b border-light-border dark:border-navy-700/60 text-gray-500 dark:text-metallic-silver text-xs uppercase tracking-widest">
              <th className="text-left py-3 pl-2 w-8">#</th>
              <th className="text-left py-3">Item</th>
              <th className="text-right py-3 w-24">Qty</th>
              <th className="text-left py-3 w-28">Unit</th>
              <th className="text-right py-3 w-32">Price (₦)</th>
              <th className="text-right py-3 w-32 pr-2">Total (₦)</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-16 text-gray-400 dark:text-metallic-silver/50 text-sm">
                  No entries yet. Add one above.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr
                key={idx}
                className="group border-b border-light-border dark:border-navy-800/40 hover:bg-gray-50 dark:hover:bg-navy-800/30 transition-colors"
              >
                <td className="py-2 pl-2 text-gray-400 dark:text-metallic-silver/40 text-xs">{idx + 1}</td>
                <td className="py-2">
                  <EditableCell
                    value={row.item}
                    editing={editingId === idx && editField === "item"}
                    editValue={editVal}
                    onStart={() => startEdit(idx, "item", row.item)}
                    onChange={setEditVal}
                    onCommit={() => commitEdit(idx)}
                  />
                </td>
                <td className="py-2 text-right">
                  <EditableCell
                    value={String(row.quantity)}
                    editing={editingId === idx && editField === "quantity"}
                    editValue={editVal}
                    onStart={() => startEdit(idx, "quantity", row.quantity)}
                    onChange={setEditVal}
                    onCommit={() => commitEdit(idx)}
                    numeric
                  />
                </td>
                <td className="py-2">
                  {editingId === idx && editField === "unit" ? (
                    <UnitCombobox
                      value={editVal}
                      onChange={setEditVal}
                      onBlur={() => commitEdit(idx)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitEdit(idx);
                        if (e.key === "Escape") commitEdit(idx);
                      }}
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={() => startEdit(idx, "unit", row.unit)}
                      className="cursor-pointer rounded-lg px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-navy-700/50 transition-colors min-h-[2rem] flex items-center text-gray-800 dark:text-white"
                    >
                      {row.unit}
                    </div>
                  )}
                </td>
                <td className="py-2 text-right">
                  <EditableCell
                    value={formatNum(row.unit_price)}
                    editing={editingId === idx && editField === "unit_price"}
                    editValue={editVal}
                    onStart={() => startEdit(idx, "unit_price", row.unit_price)}
                    onChange={setEditVal}
                    onCommit={() => commitEdit(idx)}
                    numeric
                  />
                </td>
                <td className="py-2 text-right font-semibold text-citilux-blue-600 dark:text-citilux-blue-400 pr-2 tabular-nums">
                  {formatNum(row.total)}
                </td>
                <td className="py-2 text-center">
                  {confirmIdx === idx ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => deleteRow(idx)}
                        className="text-red-600 dark:text-red-400 text-xs font-semibold px-1.5 py-0.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20"
                      >
                        Del
                      </button>
                      <button
                        onClick={() => setConfirmIdx(null)}
                        className="text-gray-500 dark:text-metallic-silver text-xs px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-white/5"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmIdx(idx)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 dark:text-metallic-silver/50 hover:text-red-600 dark:hover:text-red-400 transition-all p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function formatNum(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function EditableCell({
  value,
  editing,
  editValue,
  onStart,
  onChange,
  onCommit,
  numeric,
}: {
  value: string;
  editing: boolean;
  editValue: string;
  onStart: () => void;
  onChange: (v: string) => void;
  onCommit: () => void;
  numeric?: boolean;
}) {
  if (editing) {
    return (
      <input
        type="text"
        value={editValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit();
          if (e.key === "Escape") onCommit();
        }}
        autoFocus
        className={`w-full bg-white dark:bg-navy-700 border border-citilux-blue-500 rounded-lg px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none ${
          numeric ? "text-right tabular-nums" : ""
        }`}
      />
    );
  }
  return (
    <div
      onClick={onStart}
      className={`cursor-pointer rounded-lg px-2 py-1 -mx-2 hover:bg-gray-100 dark:hover:bg-navy-700/50 transition-colors min-h-[2rem] flex items-center text-gray-800 dark:text-white ${
        numeric ? "justify-end tabular-nums" : ""
      }`}
    >
      {value}
    </div>
  );
}
